import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  useMediaQuery,
  GlobalStyles,
  Grid,
  TextField,
} from '@mui/material';
import conferenceHeader from '../images/conference_header.png';
import { useTheme } from '@mui/material/styles';
import SubHeader from 'components/Conference/SubHeader';
import { Helmet } from 'react-helmet-async';
import { useToast } from 'toastContext';
import { Configuration, ConferenceApi } from 'api';
import config from 'components/Config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'authContext';
import { UserRole } from 'core/constants';
import { useUserInfo } from 'userInfoContext';
import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';

const CONFERENCE_DATE = new Date('2025-05-01');

const LastMinuteSignup = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const { role, userInfo } = useUserInfo();

  const [currentResponseLocation, setCurrentResponseLocation] = useState('');
  const [currentResponseFoodPreferences, setResponseFoodPreferences] = useState('');
  const [currentResponseLastModifiedAt, setResponseLastModifiedAt] = useState('');

  const [selectedLocation, setSelectedLocation] = useState('');
  const [foodPreferences, setFoodPreferences] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubscriptionValid, setIsSubscriptionValid] = useState(false);

  const configuration = new Configuration({
    basePath: config.API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const conferenceApi = new ConferenceApi(configuration);

  const location = useLocation();

  // scroll to top of page after a page transition.
  useLayoutEffect(() => {
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  useEffect(() => {
    fetchUserSelection();
    checkSubscriptionValidity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, userInfo?.subscriptionEndDate]);

  const fetchUserSelection = () => {
    conferenceApi
      .conferenceSignupGet()
      .then(resp => {
        if (resp) {
          setCurrentResponseLocation(resp.location);
          setResponseFoodPreferences(resp.foodPreferences || '');
          setResponseLastModifiedAt(resp.lastModifiedAt);

          setSelectedLocation(resp.location);
          setFoodPreferences(resp.foodPreferences || '');
        }
      })
      .catch(error => {
        if (error.status === 401) {
          logout(() => navigate('/login'));
        } else if (error.status === 404) {
          console.log('user did not make selection yet.');
        } else {
          showToast('Failed to fetch conference signup details.', 'error');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const checkSubscriptionValidity = () => {
    if (!userInfo?.subscriptionEndDate) {
      setIsSubscriptionValid(false);
      return;
    }

    if (userInfo?.subscriptionEndDate >= CONFERENCE_DATE) {
      setIsSubscriptionValid(true);
    } else {
      setIsSubscriptionValid(false);
    }
  };

  const handleChange = event => {
    setSelectedLocation(event.target.value);
    if (event.target.value !== 'Gothenburg' && event.target.value !== 'Stockholm') {
      setFoodPreferences('');
    } else {
      setFoodPreferences(currentResponseFoodPreferences);
    }
  };

  const handleConfirm = () => {
    if (!selectedLocation) {
      showToast('Please select a location before confirming.', 'error');
      return;
    }

    conferenceApi
      .conferenceSignupPost({
        conferenceSignupRequest: {
          location: selectedLocation,
          foodPreferences: foodPreferences,
        },
      })
      .then(() => {
        showToast('Your RSVP has been updated!');
        fetchUserSelection();
      })
      .catch(error => {
        if (error.status === 401) {
          logout(() => navigate('/login'));
        } else {
          showToast('Failed to update your RSVP.', 'error');
        }
      });
  };

  const getYourAttendenceDescritpion = location => {
    const locationMap = {
      Gothenburg: 'Joining Gothenburg May 13th conference!',
      Stockholm: 'Joining Stockholm May 7th conference!',
      OnlineGothenburg: 'Joining Online May 13th event',
      OnlineStockholm: 'Joining Online May 7th event',
      Resigned: 'Not attending',
    };
    return locationMap[location] || location;
  };

  return (
    <Box>
      <Helmet>
        <title>Last Minute Signup</title>
      </Helmet>
      <GlobalStyles styles={{ body: { backgroundColor: '#F5F5F5' } }} />
      <SubHeader />
      <Box
        sx={{
          backgroundImage: `url(${conferenceHeader})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '189px',
          width: '100%',
          position: 'relative',
          clipPath: 'polygon(0 0, 100% 0, 100% 80px, 0 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <Typography
            sx={{
              fontSize: isSmallScreen ? '32px' : '49px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              lineHeight: '1.1',
              pt: '20px',
            }}
          >
            Last Minute Signup <br /> 2025
          </Typography>
        </Container>
      </Box>
      <Container maxWidth="md" sx={{ mb: 4 }}>
        {role === UserRole.ELEVATE && !isSubscriptionValid && (
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Box sx={{ backgroundColor: '#FFE0E0', p: 3, borderRadius: 2, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 'bold', color: '#D32F2F' }}>
                Your subscription expires before the conference
              </Typography>
              <Typography sx={{ mt: 1 }}>
                To attend the Ambition Conference, your **Empower membership must be active** on May 7, 2025.
              </Typography>
              <Typography sx={{ mt: 1 }}>
                Please{' '}
                <a
                  href={`mailto:empower@ambitiongrp.com?subject=Renew my Empower Membership`}
                  style={{ textDecoration: 'none', fontWeight: 'bold' }}
                >
                  renew
                </a>{' '}
                your subscription to secure your spot.
              </Typography>
            </Box>
          </Grid>
        )}
        <Grid container spacing={4} direction={'column'}>
          {role === UserRole.ELEVATE && isSubscriptionValid && (
            <Grid>
              <Box
                elevation={3}
                sx={{
                  p: 2,
                  maxWidth: 350,
                  mx: 'auto',
                  mt: 5,
                  backgroundColor: '#EFC990',
                  clipPath: 'polygon(0 0px, 100% 0, 100% 100%, 0% 100%)',
                }}
              >
                <Typography sx={{ fontSize: '16px', textAlign: 'right', fontWeight: 'bold', my: 2 }}>Signup</Typography>
                <RadioGroup value={selectedLocation} onChange={handleChange}>
                  <FormControlLabel
                    value="Stockholm"
                    control={
                      <Radio
                        sx={{
                          color: '#582E4F',
                        }}
                      />
                    }
                    label="Stockholm May 7th"
                  />
                  <FormControlLabel
                    value="Gothenburg"
                    control={
                      <Radio
                        sx={{
                          color: '#582E4F',
                        }}
                      />
                    }
                    label="Gothenburg May 13th"
                  />
                  <FormControlLabel
                    value="OnlineStockholm"
                    control={
                      <Radio
                        sx={{
                          color: '#582E4F',
                        }}
                      />
                    }
                    label="Online May 7th"
                  />
                  <FormControlLabel
                    value="OnlineGothenburg"
                    control={
                      <Radio
                        sx={{
                          color: '#582E4F',
                        }}
                      />
                    }
                    label="Online May 13th"
                  />

                  <FormControlLabel
                    value="Resigned"
                    control={
                      <Radio
                        sx={{
                          color: '#582E4F',
                        }}
                      />
                    }
                    label="Not attending"
                  />
                </RadioGroup>
                {(selectedLocation === 'Gothenburg' || selectedLocation === 'Stockholm') && (
                  <TextField
                    label="Food allergies"
                    variant="filled"
                    fullWidth
                    sx={{ my: 2 }}
                    value={foodPreferences}
                    onChange={e => setFoodPreferences(e.target.value)}
                  />
                )}
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mb: 4, mt: 2 }}
                  onClick={handleConfirm}
                  disabled={
                    !loading &&
                    currentResponseLocation === selectedLocation &&
                    currentResponseFoodPreferences === foodPreferences
                  }
                >
                  {selectedLocation ? 'Update RSVP' : 'Confirm'}
                </Button>
              </Box>

              <Box
                elevation={2}
                sx={{
                  p: 2,
                  pb: 4,
                  maxWidth: 350,
                  mx: 'auto',
                  mt: '-42px',
                  backgroundColor: '#E9DED2',
                  clipPath:
                    selectedLocation === 'Gothenburg' || selectedLocation === 'Stockholm'
                      ? 'polygon(0 30px, 100% 0, 100% 85%, 0% 100%)'
                      : 'polygon(0 30px, 100% 0, 100% 75%, 0% 100%)',
                }}
              >
                {currentResponseLastModifiedAt ? (
                  <>
                    <Typography sx={{ fontWeight: 'bold', mt: 4, pb: 2 }}>
                      {getYourAttendenceDescritpion(currentResponseLocation)}
                    </Typography>
                    {(currentResponseLocation === 'Gothenburg' || currentResponseLocation === 'Stockholm') && (
                      <Typography sx={{ mb: 4, fontSize: '14px' }}>
                        Note there is a no show fee of 100€ if you do not attend, so please update your RSVP before
                        2025-04-13 if you will not attend.
                      </Typography>
                    )}
                    <Typography sx={{ mb: 0, fontSize: '14px', opacity: '60%' }}>Response registered:</Typography>
                    <Typography sx={{ mb: 4, fontSize: '14px', opacity: '60%' }}>
                      {format(currentResponseLastModifiedAt, 'd MMMM HH:mm (O)')}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontWeight: 'bold', mt: 4, mb: 1 }}>You have not signed up yet.</Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default LastMinuteSignup;
